import { useCallback, useEffect, useRef, useState } from "react";
import { getAllTags, getTagsPaginated } from "modules/tags/services/tagService";
export function countConsoleStates(rows) {
  const counts = new Map();
  rows.forEach((tag) => {
    const name = String(tag.statusconsole || "Desconocido");
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  return [...counts].map(([status_console, count_status]) => ({ status_console, count_status }));
}
export function selectTagRows(rows, search, sort) {
  const clean = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const needle = clean(search.trim());
  const filtered = needle
    ? rows.filter((tag) =>
        [
          tag.id,
          tag.qr,
          tag.reverse_number,
          tag.code,
          tag.epc,
          tag.statusconsole,
          tag.status,
          tag.lot,
          tag.userId,
          tag.createdAtFormatted,
        ].some((value) => clean(value).includes(needle))
      )
    : [...rows];
  if (sort.field)
    filtered.sort((a, b) => {
      const field = sort.field;
      const av = a[field],
        bv = b[field];
      if (av == null) return bv == null ? 0 : 1;
      if (bv == null) return -1;
      const result =
        field === "createdAt"
          ? new Date(av).getTime() - new Date(bv).getTime()
          : String(av).localeCompare(String(bv), "es", { numeric: true });
      return sort.direction === "asc" ? result : -result;
    });
  return filtered;
}
export function useTagsTable({ lot, refreshFlag, onError }) {
  const [lotFilter, setLotFilter] = useState(lot == null ? "" : String(lot));
  const [statusFilter, setStatusFilter] = useState("todos");
  const [search, setSearch] = useState("");
  const [criteria, setCriteria] = useState({ lot: lotFilter, status: "todos", search: "" });
  const [sort, setSort] = useState({ field: "", direction: "asc" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [version, setVersion] = useState(0);
  const [state, setState] = useState({
    rows: [],
    total: 0,
    loading: true,
    error: "",
    consoleStats: [],
    progress: null,
  });
  const notify = useRef(onError);
  const cache = useRef(null);
  const revision = useRef(0);
  useEffect(() => {
    notify.current = onError;
  }, [onError]);
  useEffect(() => {
    setLotFilter(lot == null ? "" : String(lot));
  }, [lot]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setCriteria({ lot: lotFilter.trim(), status: statusFilter, search: search.trim() });
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [lotFilter, statusFilter, search]);
  useEffect(() => {
    const id = ++revision.current;
    const controller = new AbortController();
    const active = () => id === revision.current && !controller.signal.aborted;
    const load = async () => {
      setState((previous) => ({ ...previous, loading: true, error: "", progress: null }));
      try {
        if (
          criteria.lot &&
          (!/^\d+$/.test(criteria.lot) ||
            Number(criteria.lot) <= 0 ||
            !Number.isSafeInteger(Number(criteria.lot)))
        )
          throw new Error("El lote debe ser un número entero positivo.");
        const params = {
          lot: criteria.lot ? Number(criteria.lot) : null,
          status: criteria.status === "todos" ? null : criteria.status,
          signal: controller.signal,
        };
        let result, rows, total, consoleStats;
        if (criteria.search || sort.field) {
          const key = JSON.stringify([params.lot, params.status, version, refreshFlag]);
          if (cache.current?.key === key) result = cache.current.result;
          else {
            result = await getAllTags({
              ...params,
              onProgress: (progress) => {
                if (active()) setState((previous) => ({ ...previous, progress }));
              },
            });
            if (active()) cache.current = { key, result };
          }
          const selected = selectTagRows(result.data, criteria.search, sort);
          consoleStats = countConsoleStates(selected);
          total = selected.length;
          rows = selected.slice(page * pageSize, (page + 1) * pageSize);
        } else {
          result = await getTagsPaginated({ ...params, offset: page * pageSize, limit: pageSize });
          if (!result.success) throw new Error(result.error);
          rows = result.data;
          total = result.total;
          consoleStats = result.consoleStats || [];
          const statsTotal = consoleStats.reduce(
            (sum, item) => sum + Number(item.count_status || 0),
            0
          );
          if (rows.length === total) consoleStats = countConsoleStates(rows);
          else if (statsTotal !== total && (criteria.lot || criteria.status !== "todos")) {
            // Algunos servidores devuelven estadísticas globales incluso con filtros.
            // Recontar el conjunto completo filtrado, nunca solo la página visible.
            const key = JSON.stringify([params.lot, params.status, version, refreshFlag]);
            const full =
              cache.current?.key === key
                ? cache.current.result
                : await getAllTags({
                    ...params,
                    onProgress: (progress) => {
                      if (active()) setState((previous) => ({ ...previous, progress }));
                    },
                  });
            if (active()) cache.current = { key, result: full };
            consoleStats = countConsoleStates(full.data);
            total = full.data.length;
            rows = full.data.slice(page * pageSize, (page + 1) * pageSize);
          }
        }
        if (!active()) return;
        const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setState({
          rows,
          total,
          loading: false,
          error: "",
          consoleStats,
          progress: null,
        });
        notify.current?.(null);
      } catch (error) {
        if (!active()) return;
        setState((previous) => ({
          ...previous,
          loading: false,
          error: error.message || "No se pudieron consultar los tags.",
          progress: null,
        }));
        notify.current?.(error.message);
      }
    };
    load();
    return () => controller.abort();
  }, [criteria, sort, page, pageSize, version, refreshFlag]);
  const refresh = useCallback(() => {
    cache.current = null;
    setVersion((value) => value + 1);
  }, []);
  const clear = () => {
    setLotFilter("");
    setStatusFilter("todos");
    setSearch("");
    setSort({ field: "", direction: "asc" });
    setPage(0);
  };
  return {
    ...state,
    lotFilter,
    setLotFilter,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    criteria,
    sort,
    setSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    refresh,
    clear,
  };
}
